<template>
  <div class="cn-product-shops">
    <h2 class="cn-product-shops__title">
      {{ $t('where_to_buy_modal_title') }}
    </h2>
    <ul v-if="shops && shops.length" class="cn-product-shops__list">
      <li v-for="(shop, i) in renderedShops" :key="i">
        <div class="cn-product-shops__item">
          <div class="cn-product-shops__item-left">
            <CdnImg
              :src="getImage(shop.image)"
              format="webp"
              loading="lazy"
              :width="56"
              :height="56"
            />
            <div class="cn-product-shops__item-name">
              {{ shop.name }}
            </div>
          </div>
          <div class="cn-product-shops__item-right">
            <div v-if="shop.price" class="cn-product-shops__item-price">
              {{ shop.currency }} {{ Math.round(shop.price) }}
            </div>
            <UiButton :to="shop.url" variant="yellow">
              {{ $t('shops_buy_text') }}
            </UiButton>
          </div>
        </div>
      </li>
    </ul>
    <UiModal
      v-if="lgAndLarger"
      ref="shopsModal"
      :title="$t('where_to_buy_modal_title')"
    >
      <ul class="cn-product-shops__list--modal">
        <li v-for="(shop, i) in props.shops" :key="i">
          <div class="cn-product-shops__item">
            <div class="cn-product-shops__item-left">
              <CdnImg
                :src="getImage(shop.image)"
                format="webp"
                loading="lazy"
                :width="56"
                :height="56"
              />
              <div class="cn-product-shops__item-name">
                {{ shop.name }}
              </div>
            </div>
            <div class="cn-product-shops__item-right">
              <div v-if="shop.price" class="cn-product-shops__item-price">
                {{ shop.currency }} {{ Math.round(shop.price) }}
              </div>
              <UiButton :to="shop.url" variant="yellow">
                {{ $t('shops_buy_text') }}
              </UiButton>
            </div>
          </div>
        </li>
      </ul>

      <p
        class="cn-product-shops__link text-gray-dark"
        v-html="$t('where_to_buy_modal_text')"
      />
    </UiModal>

    <button
      v-if="isCollapsible"
      class="cn-product-shops__more-button"
      :class="{
        'cn-product-shops__more-button--hide':
          !lgAndLarger && isCollapsedItemsShowed,
      }"
      @click="getClickHandlerFunction()"
    >
      <UiIcon name="arrow-down" />
      <span v-if="!isCollapsedItemsShowed || lgAndLarger">
        {{ $t('show_all') }} ({{ shops.length }})
      </span>
      <span v-else-if="!lgAndLarger">{{ $t('hide') }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { CdnImg } from '@webplatform/cdn-image';
import { useBreakpoints } from '@vueuse/core';
import UiModal from '@/components/ui/UiModal';
import UiIcon from '@/components/ui/UiIcon';
import UiButton from '@/components/ui/UiButton';

interface Props {
  shops: any[];
}

type ShopsModalType = InstanceType<typeof UiModal>;

const props = defineProps<Props>();

const breakpoints = useBreakpoints({
  sm: 640,
  md: 992,
  lg: 1280,
  xl: 1656,
});
const lgAndLarger = breakpoints.greater('lg');

const shopsModal = ref<ShopsModalType | null>(null);

const getImage = (image?: string) =>
  image !== undefined && image !== '' ? image : '/img/shop1.jpg';
const isCollapsible = computed(() => props.shops.length > 4);
const isCollapsedItemsShowed = ref(false);
const renderedShops = computed(() => {
  if (!isCollapsible.value) return props.shops;
  if (isCollapsedItemsShowed.value) return props.shops;

  return props.shops.slice(0, 4);
});

const openShopsModal = (shopsModalRef: ShopsModalType | null) => {
  if (!shopsModalRef) return;
  shopsModalRef.open();
};

const toggleCollapsedItems = () => {
  isCollapsedItemsShowed.value = !isCollapsedItemsShowed.value;
};

const getClickHandlerFunction = () => {
  return !lgAndLarger.value
    ? toggleCollapsedItems()
    : openShopsModal(shopsModal.value);
};

watch(
  () => lgAndLarger.value,
  (newValue) => {
    // Reset isCollapsedItemsShowed to avoid state when full list is showed and modal can be opened on desktop
    if (newValue) isCollapsedItemsShowed.value = false;
  },
);
</script>

<style lang="scss">
.cn-product-shops {
  --ab-canyon-product-shops-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-product-shops-item-color-border: var(--ab-canyon-color-neutral);
  --ab-canyon-product-shops-more-button-color-text: var(
    --ab-canyon-color-accent
  );

  margin-top: calc(var(--size-base) / 2);
  margin-bottom: var(--size-4xl);
  display: flex;
  flex-direction: column;
  gap: var(--size-lg);
  @media (min-width: 640px) {
    margin-bottom: 0;
    margin-top: 28px;
  }

  &__title {
    font-weight: 700;
    font-size: 1.125rem;
    line-height: 1.75rem;
    color: var(--ab-canyon-product-shops-color-text);
    order: 0;
  }

  &__list {
    order: 2;
  }
  &__item {
    display: flex;
    align-items: center;
    padding-bottom: var(--size-xs);
    border-bottom: 1px solid var(--ab-canyon-product-shops-item-color-border);
    @media (min-width: 640px) {
      padding-bottom: var(--size-base);
      padding-left: calc(var(--size-base) / 2);
      padding-right: calc(var(--size-base) / 2);
    }
  }
  &__item-left {
    display: flex;
    align-items: center;
    margin-right: auto;
    padding-right: calc(var(--size-base) / 4);
    img {
      flex-shrink: 0;
    }
  }
  &__item-name {
    color: var(--ab-canyon-product-shops-color-text);
    display: none;
    margin-left: var(--size-base);
    font-size: 18px;
    line-height: 28px;
    font-weight: 400;
    @media (min-width: 1140px) {
      display: block;
    }
  }
  &__item-price {
    color: var(--ab-canyon-product-shops-color-text);
    margin-right: 14px;
    font-size: 18px;
    line-height: 28px;
    font-weight: 400;
  }
  &__item-right {
    display: flex;
    align-items: center;
  }
  &__more-button {
    color: var(--ab-canyon-product-shops-more-button-color-text);
    font-size: 1.125rem;
    line-height: 1.75rem;
    column-gap: calc(var(--size-base) / 2);
    align-items: center;
    display: inline-flex;
    order: 3;

    .cn-icon {
      transition-property: transform;
      transition-duration: 150ms;
      width: var(--size-base);
      height: var(--size-base);
    }
    &--hide {
      .cn-icon {
        transform: rotate(180deg);
      }
    }
  }
  ul {
    li {
      &:not(:last-child) {
        margin-bottom: var(--size-xs);
        @media (min-width: 640px) {
          margin-bottom: var(--size-base);
        }
      }
    }
  }

  &__list {
    &--modal {
      max-height: 54vh;
      overflow-y: auto;
    }
  }

  &__link {
    margin-top: var(--size-4xl);

    a {
      color: var(--ab-canyon-color-accent);
      text-decoration: underline;
    }
  }

  @media (min-width: 1280px) {
    flex-direction: row;
    height: min-content;
    flex-wrap: wrap;
    gap: 16px;

    &__title {
      flex-grow: 1;
      order: 0;
    }

    &__more-button {
      flex-grow: 0;
      order: 1;
    }

    &__list {
      width: 100%;
      order: 3;
    }
  }
}
</style>
